import { useState } from 'react'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import { useHistory } from 'react-router'
import { Inline } from '@machobear/design-system'
import ChatNotification from '@/components/chat-notification'
import config from '@/config'
import useMediaQuery from '@/hooks/use-media-query'
import { useAvailableCreditChecks } from '@/hooks/use-block-and-pay'
import { colors, breakpoints, structure, zIndex } from '@/styles'
import { useAppWebView } from '@/hooks/use-app-webview'
import { LogoIcon } from '@/components/icons'
import BackButton from '@/components/button/back-button'
import { ChevronLeft } from '@/components/icons'
import useRole from '@/hooks/use-role'
import DashboardHeaderTitle from './dashboard-header-title'
import MenuIcon from './menu-icon.svg?react'
import NavDropDown from './nav-drop-down'
import Dropdown from './dropdown'
import TsDropdown from './ts-dropdown'
import PromotionList from '../promotion-list'

const { WEB_URL } = config

const StyledChatNotification = styled(ChatNotification)`
  width: auto;
  height: auto;
`

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  height: ${structure.header.height}px;
  z-index: ${zIndex.header};
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.white};
  transition: all 0.2s ease;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: ${structure.header.heightMobile}px;
    width: 100%;
  }

  @media print {
    position: static;
  }
`

const HeaderInner = styled.div`
  display: flex;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
  align-items: center;
  justify-content: flex-start;
  padding: 0 32px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    justify-content: flex-start;
    align-items: center;
    padding: 0 20px;
  }
`

const CustomBackButtonContainer = styled.div``

const LogoLink = styled.a`
  align-self: center;
  padding-right: env(safe-area-inset-right, 0px);
  height: 24px;
  display: none;
  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    display: block;
  }
`

const HamburguerMenu = styled.button`
  border: none;
  background: none;
  flex-shrink: 0;
  padding: 0;
  min-width: 52px;
  cursor: pointer;
  svg {
    display: block;
  }
  :focus,
  :hover {
    opacity: 0.5;
  }

  > svg {
    margin-left: auto;
  }
`

const DividerContainer = styled.div`
  height: 1px;
  width: 100%;
  overflow-y: hidden;
`

const CustomChildContainer = styled(DividerContainer)`
  height: unset;
`

const SETTINGS_PATHS = [
  '/settings/plan-settings',
  '/settings/payment-settings',
  '/manage/users',
  '/manage/listings',
  '/settings/notifications',
  '/settings/preferences',
  '/settings/change-phone-number',
  '/settings/social-login-password',
  '/settings/feedback',
  '/settings/support'
]
const SETTINGS_SUB_PATHS = ['/settings/delete-account']
const LANDLORD_PATHS = ['/listing/', '/tenant/', '/trust-score/', '/settings/auto-reply']
const STANDALONE_PATHS = ['/trust-score/credit-check', '/trust-score/edit/']
const STANDALONE_STEPS = ['credit-check', 'tenant-screening', 'payment', 'confirmation']
const CUSTOM_BACK_BUTTON_PATHS = ['/listing/create', '/listing/edit']

const Header = ({ setSidebar }) => {
  const { pathname } = useLocation()
  const history = useHistory()
  const role = useRole()
  const belowDesktop = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const toggleSidebar = () => setSidebar(current => !current)
  const [isOpen, setIsOpen] = useState(false)
  const [isTrustScoreOpen, setIsTrustScoreOpen] = useState(false)
  const [setReferenceElement] = useState(null)

  const { availableCreditChecks } = useAvailableCreditChecks()
  const hasAvailableCreditChecks = availableCreditChecks > 0
  const showPayment = !hasAvailableCreditChecks
  const isLandlord = role === 'landlord'
  const hasHistory = window.history.length > 1

  const showCustomBackButton =
    belowDesktop && CUSTOM_BACK_BUTTON_PATHS.some(link => pathname.includes(link))

  const showBackButton =
    belowDesktop &&
    hasHistory &&
    [...SETTINGS_PATHS, ...SETTINGS_SUB_PATHS, ...LANDLORD_PATHS].some(link =>
      pathname.includes(link)
    )

  const handleBackButton = () => {
    const isSettingsPath = SETTINGS_PATHS.some(link => pathname === link)
    const isStandAlonePath = isLandlord && STANDALONE_PATHS.some(link => pathname.includes(link))
    if (isSettingsPath) {
      return history.push('/settings')
    }

    if (isStandAlonePath) {
      const steps = !showPayment
        ? STANDALONE_STEPS.filter(step => step !== 'payment')
        : STANDALONE_STEPS

      const currentStep = pathname.split('/').slice(-1)[0]
      const previsousStep = steps[steps.indexOf(currentStep) - 1]

      return history.push(
        currentStep === 'credit-check'
          ? '/trust-score'
          : pathname.replace(`/${currentStep}`, `/${previsousStep}`)
      )
    }

    history.goBack()
  }

  window.onresize = () => {
    if (window.innerWidth < 768) {
      setIsOpen(false)
      setIsTrustScoreOpen(false)
    }
  }

  const isApp = useAppWebView()
  if (isApp) return null
  return (
    <>
      {isOpen && <Dropdown isOpen={isOpen} setIsOpen={setIsOpen} />}
      {isTrustScoreOpen && <TsDropdown isOpen={isTrustScoreOpen} setIsOpen={setIsTrustScoreOpen} />}
      <HeaderContainer>
        <HeaderInner>
          {showCustomBackButton ? (
            <CustomBackButtonContainer
              aria-hidden
              id="header-custom-back-button"
            ></CustomBackButtonContainer>
          ) : showBackButton ? (
            <BackButton
              color={colors.black}
              gap="4px"
              fontSize="12px"
              onClick={handleBackButton}
              Icon={ChevronLeft}
              padding="0"
            />
          ) : (
            <LogoLink as="a" href={WEB_URL}>
              <LogoIcon aria-label="Home" alt="Liv.rent Logo" />
            </LogoLink>
          )}
          <DashboardHeaderTitle />
          <Inline space={{ tablet: 'gutter', mobile: 'none' }} alignY="center">
            {role === 'landlord' && <PromotionList />}
            {belowDesktop ? (
              <HamburguerMenu onClick={toggleSidebar} type="button">
                <MenuIcon />
                <StyledChatNotification top="-26.5px" right="-7px" />
              </HamburguerMenu>
            ) : (
              <NavDropDown
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setAltOpen={setIsTrustScoreOpen}
                setReferenceElement={setReferenceElement}
              />
            )}
          </Inline>
        </HeaderInner>
        <DividerContainer aria-hidden id="header-divider"></DividerContainer>
        <CustomChildContainer aria-hidden id="header-custom-child"></CustomChildContainer>
      </HeaderContainer>
    </>
  )
}

export default Header
